import {
    ProductPickerContext,
    ProductPickerItem,
    ProductPickerMode,
    ProductPickerModesMap,
    PRODUCT_PICKER_CONTEXTS,
    PRODUCT_PICKER_MODES,
} from './types';

const STORAGE_KEY = 'visor_product_picker_mode';
const STORAGE_KEY_PREFIX = 'visor_product_picker_mode:';

export const DEFAULT_PRODUCT_PICKER_MODE: ProductPickerMode = 'catalog_panel';

export const DEFAULT_PRODUCT_PICKER_MODES: Record<ProductPickerContext, ProductPickerMode> = {
    orders: DEFAULT_PRODUCT_PICKER_MODE,
    operations: DEFAULT_PRODUCT_PICKER_MODE,
    purchases: DEFAULT_PRODUCT_PICKER_MODE,
    transfers: DEFAULT_PRODUCT_PICKER_MODE,
};

export const isProductPickerMode = (value: unknown): value is ProductPickerMode =>
    typeof value === 'string' && (PRODUCT_PICKER_MODES as string[]).includes(value);

export const isProductPickerContext = (value: unknown): value is ProductPickerContext =>
    typeof value === 'string' && (PRODUCT_PICKER_CONTEXTS as string[]).includes(value);

const storageKeyFor = (context?: ProductPickerContext) =>
    context ? `${STORAGE_KEY_PREFIX}${context}` : STORAGE_KEY;

export const getStoredProductPickerMode = (context?: ProductPickerContext): ProductPickerMode => {
    try {
        if (context) {
            const scoped = localStorage.getItem(storageKeyFor(context));
            if (isProductPickerMode(scoped)) {
                return scoped;
            }
        }
        const stored = localStorage.getItem(STORAGE_KEY);
        if (isProductPickerMode(stored)) {
            return stored;
        }
    } catch {
        // ignore
    }
    return DEFAULT_PRODUCT_PICKER_MODE;
};

export const setStoredProductPickerMode = (
    mode: ProductPickerMode,
    context?: ProductPickerContext,
) => {
    try {
        localStorage.setItem(storageKeyFor(context), mode);
        if (!context) {
            localStorage.setItem(STORAGE_KEY, mode);
        }
        window.dispatchEvent(
            new CustomEvent('visor:product-picker-mode', {
                detail: {mode, context},
            }),
        );
    } catch {
        // ignore
    }
};

const CATALOG_SHOW_IMAGES_KEY = 'visor_product_picker_catalog_show_images';
const TILES_SHOW_IMAGES_KEY = 'visor_product_picker_tiles_show_images';

/** Default off — photos are the main cost on large catalogs */
export const getStoredCatalogShowImages = (): boolean => {
    try {
        const stored = localStorage.getItem(CATALOG_SHOW_IMAGES_KEY);
        if (stored === null) return false;
        return stored === '1' || stored === 'true';
    } catch {
        return false;
    }
};

export const setStoredCatalogShowImages = (show: boolean) => {
    try {
        localStorage.setItem(CATALOG_SHOW_IMAGES_KEY, show ? '1' : '0');
    } catch {
        // ignore
    }
};

export const getStoredTilesShowImages = (): boolean => {
    try {
        const stored = localStorage.getItem(TILES_SHOW_IMAGES_KEY);
        if (stored === null) return true;
        return stored === '1' || stored === 'true';
    } catch {
        return true;
    }
};

export const setStoredTilesShowImages = (show: boolean) => {
    try {
        localStorage.setItem(TILES_SHOW_IMAGES_KEY, show ? '1' : '0');
    } catch {
        // ignore
    }
};

export const normalizeProductPickerModes = (
    modes?: Partial<Record<ProductPickerContext, string>> | null,
    fallback?: string,
): Record<ProductPickerContext, ProductPickerMode> => {
    const baseMode = isProductPickerMode(fallback) ? fallback : DEFAULT_PRODUCT_PICKER_MODE;
    const result: Record<ProductPickerContext, ProductPickerMode> = {
        orders: baseMode,
        operations: baseMode,
        purchases: baseMode,
        transfers: baseMode,
    };

    PRODUCT_PICKER_CONTEXTS.forEach((context) => {
        const value = modes?.[context];
        if (isProductPickerMode(value)) {
            result[context] = value;
        }
    });

    return result;
};

export const resolveProductPickerMode = (params: {
    mode?: ProductPickerMode;
    context?: ProductPickerContext;
    modes?: Partial<Record<ProductPickerContext, string>> | null;
    legacyMode?: string;
    storedMode?: ProductPickerMode;
}): ProductPickerMode => {
    if (isProductPickerMode(params.mode)) {
        return params.mode;
    }

    const normalized = normalizeProductPickerModes(params.modes, params.legacyMode);
    if (params.context && isProductPickerMode(normalized[params.context])) {
        return normalized[params.context];
    }

    if (isProductPickerMode(params.legacyMode)) {
        return params.legacyMode;
    }

    if (isProductPickerMode(params.storedMode)) {
        return params.storedMode;
    }

    return getStoredProductPickerMode(params.context);
};

export const normalizeProductPickerItem = (product: any): ProductPickerItem => {
    // ProductPicker always re-normalizes; keep the original payload if already wrapped
    const source = product?.raw && typeof product.raw === 'object' ? product.raw : product;

    const images = Array.isArray(product?.images)
        ? product.images
        : Array.isArray(source?.images)
            ? source.images
            : Array.isArray(source?.images_arr)
                ? source.images_arr
                : (source?.image ? [source.image] : []);

    const quantityInHandRaw = product?.quantity_in_hand
        ?? source?.quantity_in_hand
        ?? source?.quantity_in_stock
        ?? source?.quantity;
    const quantityInHand = quantityInHandRaw === undefined || quantityInHandRaw === null
        ? undefined
        : Number(quantityInHandRaw);

    const id = Number(product?.id || source?.id || source?.goods_id || source?.product_id) || 0;
    const name = String(product?.name || source?.name || source?.product_name || '');
    const partNumber = product?.part_number || source?.part_number || '';
    const brand = product?.brand || source?.brand || source?.brand_name || '';
    const category = product?.category || source?.category || '';
    let categoryId = Number(product?.category_id ?? source?.category_id) || 0;
    if (!categoryId && category) {
        categoryId = hashCategoryId(category);
    }

    const hasSerial = Boolean(source?.has_serial ?? product?.has_serial);

    const item: ProductPickerItem = {
        id,
        name,
        part_number: partNumber,
        brand,
        // Keep list payload light — full text stays on raw if needed
        description: '',
        category_id: categoryId,
        category,
        last_price: Number(product?.last_price ?? source?.last_price ?? source?.set_price ?? 0),
        set_price: Number(product?.set_price ?? source?.set_price ?? source?.last_price ?? 0),
        has_variation: Boolean(product?.has_variation ?? source?.has_variation),
        has_serial: hasSerial,
        variations_combinations: Array.isArray(product?.variations_combinations)
            ? product.variations_combinations.map((v: any) => ({
                id: Number(v.id),
                display_name: String(v.display_name || v.name || `Вариация #${v.id}`),
                price: Number(v.price ?? product?.last_price ?? source?.last_price ?? source?.set_price ?? 0),
            }))
            : Array.isArray(source?.variations_combinations)
                ? source.variations_combinations.map((v: any) => ({
                    id: Number(v.id),
                    display_name: String(v.display_name || v.name || `Вариация #${v.id}`),
                    price: Number(v.price ?? source?.last_price ?? source?.set_price ?? 0),
                }))
                : [],
        images,
        quantity_in_hand: Number.isFinite(quantityInHand as number) ? quantityInHand : undefined,
        measure_unit: product?.measure_unit || source?.measure_unit || '',
        entity_type: 'goods',
        raw: source,
    };

    item.searchHaystack = [
        String(item.id),
        item.name,
        item.part_number,
        item.brand,
        item.category,
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

    return item;
};

const hashCategoryId = (name: string): number => {
    let hash = 0;
    for (let i = 0; i < name.length; i += 1) {
        hash = ((hash << 5) - hash) + name.charCodeAt(i);
        hash |= 0;
    }
    return Math.abs(hash) || 1;
};

/** Unique picker id for delegated services (same service_id, different grant). */
const DELEGATED_ID_OFFSET = 1_000_000_000;

export const normalizeServicePickerItem = (service: any): ProductPickerItem => {
    const images = Array.isArray(service?.images)
        ? service.images
        : Array.isArray(service?.images_arr)
            ? service.images_arr
            : (typeof service?.image === 'string' && service.image
                ? service.image.split(',').filter(Boolean)
                : []);

    const serviceId = Number(service?.id || service?.service_id || service?.product_id) || 0;
    const grantId = Number(service?.grant_id) || 0;
    const isDelegated = Boolean(service?.is_delegated && grantId);
    const categoryName = String(service?.category || '');
    const min = service?.min != null ? Number(service.min) : undefined;
    const max = service?.max != null ? Number(service.max) : undefined;
    const setPrice = Number(service?.set_price) || 0;
    const isFixedAmount = String(service?.amount_mode || '') === 'fixed' && setPrice > 0;
    const priceHint = isFixedAmount
        ? setPrice
        : (Number.isFinite(min as number) ? Number(min) : 0);

    const item: ProductPickerItem = {
        id: isDelegated ? DELEGATED_ID_OFFSET + grantId : serviceId,
        name: String(service?.name || service?.service_name || ''),
        part_number: '',
        brand: service?.brand || '',
        description: '',
        category_id: Number(service?.category_id) || (categoryName ? hashCategoryId(categoryName) : 0),
        category: categoryName,
        last_price: priceHint,
        set_price: isFixedAmount ? setPrice : priceHint,
        has_variation: false,
        variations_combinations: [],
        images,
        entity_type: 'service',
        raw: {
            ...service,
            id: serviceId,
            product_id: serviceId,
            type: 'service',
            name: service?.name || service?.service_name || '',
        },
        // surface min/max on raw already; optional meta via description if both set
        ...(Number.isFinite(min as number) && Number.isFinite(max as number) && max! > 0
            ? {}
            : {}),
    };

    item.searchHaystack = [
        String(item.id),
        item.name,
        item.brand,
        item.category,
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

    return item;
};

export const normalizeProductPickerList = (products: any[]): ProductPickerItem[] =>
    (products || []).map(normalizeProductPickerItem).filter((p) => p.id > 0);

/** Keep system services (id -1/-2) and remapped delegated ids; drop only invalid id 0. */
export const normalizeServicePickerList = (services: any[]): ProductPickerItem[] =>
    (services || []).map(normalizeServicePickerItem).filter((p) => p.id !== 0);

export const deriveProductPickerBrands = (products: ProductPickerItem[]): string[] => {
    const set = new Set<string>();
    products.forEach((p) => {
        if (p.brand) set.add(p.brand);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ru'));
};

export const LARGE_CATALOG_THRESHOLD = 200;

export const getProductImageUrl = (product: ProductPickerItem): string | undefined => {
    const first = product.images?.[0];
    if (!first) return undefined;
    if (typeof first === 'string') return first;
    return first.url;
};

export const getProductPrice = (product: ProductPickerItem): number =>
    Number(product.last_price ?? product.set_price ?? 0);

export const formatProductPickerPrice = (product: ProductPickerItem): string => {
    if (product.entity_type === 'service') {
        const caps = product.raw || {};
        const setPrice = Number(caps.set_price ?? product.set_price ?? 0);
        if (caps.amount_mode === 'fixed' && setPrice > 0) {
            return formatMoney(setPrice);
        }
        const min = product.raw?.min != null ? Number(product.raw.min) : NaN;
        const max = product.raw?.max != null ? Number(product.raw.max) : NaN;
        if (Number.isFinite(min) && Number.isFinite(max) && max > 0 && max !== min) {
            return `${formatMoney(min)}–${formatMoney(max)}`;
        }
        if (Number.isFinite(min) && min > 0) {
            return `от ${formatMoney(min)}`;
        }
        return '—';
    }
    return formatMoney(getProductPrice(product));
};

export const formatMoney = (value?: number): string => {
    const amount = Number(value || 0);
    return amount.toLocaleString('ru-RU', {maximumFractionDigits: 2});
};

export const hasStockQuantity = (product: ProductPickerItem): boolean =>
    product.quantity_in_hand !== undefined && product.quantity_in_hand !== null
    && Number.isFinite(Number(product.quantity_in_hand));

export const formatStockQuantity = (product: ProductPickerItem): string => {
    if (!hasStockQuantity(product)) return '';
    const qty = Number(product.quantity_in_hand);
    const formatted = Number.isInteger(qty)
        ? String(qty)
        : qty.toLocaleString('ru-RU', {maximumFractionDigits: 3});
    const unit = product.measure_unit ? ` ${product.measure_unit}` : '';
    return `${formatted}${unit}`;
};
